import { TidesService, Prediction, Station } from '../services/tides-service';
import { TidesModelBase } from './tides-model';
import { TerminalsModel } from './terminals-model';
import { CoordinatesUtils } from '../utils/coordinates-utils';
import { TerminalInfo } from './types/terminals-model.types';

const REFRESH_INTERVAL = 3600000; // One hour

export class TidesModelImpl extends TidesModelBase {
    private _stations: Station[] = [];
    private _terminalToStationMap: Map<string, Station> = new Map<string, Station>();
    private _fetchingPromise: Promise<void> | null = null;

    constructor() {
        super();

        const enableTides = (<any>window)["appenv"].featureToggles.tides;
        this._fetchStations = this._fetchStations.bind(this);

        if (enableTides) {
            this._fetchingPromise = this._fetchStations();
            TerminalsModel!.addEventListener('terminals-updated', this._fetchStations);
        }
    }

    private async _fetchStations() : Promise<void> {
        // if local development we will not be able to get navtex
        if (!(<any>window)["appenv"].featureToggles.tides) {
            return;
        }

        try {
            // Directly working with the result of the fetch call
            const stationsData: Station[] = await TidesService.fetchAllStations();
    
            // If stationsData is falsy, log a warning and return
            if (!stationsData) {
                console.warn("Tide data not available");
                return;
            }
    
            this._stations = stationsData;
    
            const terminals = TerminalsModel!.terminals;
    
            terminals.forEach((terminal: TerminalInfo, terminalId: string) => {
                let smallestDistance = 1000000;
                let targetStation: Station | null = null;
    
                for (const station of this._stations) {
                    if (station.type !== "Port" || !station.licensed) {
                        continue;
                    }
    
                    const distance = CoordinatesUtils.calcDistance([terminal.longitude, terminal.latitude], [station.longitude, station.latitude]).gc;
                    if (distance < smallestDistance) {
                        smallestDistance = distance;
                        targetStation = station;
                    }
                }
    
                this._terminalToStationMap.set(terminalId, targetStation!);
            });
    
            this.setCacheLifecycle();
            this.dispatchEvent(new Event('tide-stations-updated'));
    
            // Refresh again in an hour
            setTimeout(() => { this._fetchingPromise = this._fetchStations(); }, REFRESH_INTERVAL);
        } catch (error) {
            console.error("Tide data not available", error);
        }
    }

    async getClosestStation(terminalId: string) {
        await this._fetchingPromise;

        const station = this._terminalToStationMap.get(terminalId);
        return station!;
    }

    async getStations() {
        await this._fetchingPromise;

        return this._stations;
    }

    async getPredictionsRange(stationId: string, startDateUTC: string, endDateUTC: string): Promise<Prediction[]> {
        await this._fetchingPromise;

        const json = await TidesService.getPredictionsRange(stationId, startDateUTC, endDateUTC);
        if (!json) {
            return [];
        }

        const data: Prediction[] = json;
        return data;
    }

    async getPredictionsRangeWithInterval(stationId: string, startDateUTC: string, endDateUTC: string, interval: number): Promise<Prediction[]> {
        await this._fetchingPromise;

        const json = await TidesService.getPredictionsRangeWithInterval(stationId, startDateUTC, endDateUTC, interval);
        if (!json) {
            return [];
        }

        const data: Prediction[] = json;
        
        return data;
    }
}
